import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { memo, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  Calendar,
  ChevronRight,
  Globe2,
  GraduationCap,
  Languages,
  MapPin,
  MessageCircle,
  PlayCircle,
  Settings,
  Shield,
  Sparkles,
  Star,
  Target,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TeacherShowcaseSection } from '@/components/sections/TeacherShowcaseSection';
import { toast } from 'sonner';
import api from '@/lib/api';
import { countryCodes } from '@/lib/countries';
import { useLang } from '@/contexts/LangContext';
import { useTheme } from '@/contexts/ThemeContext';
import { buildImageUrl, formatNumber } from '@/lib/utils';
import { trackEvent } from '@/lib/tracking';

const iconMap = {
  BookOpen,
  BriefcaseBusiness,
  Building2,
  Calendar,
  Globe2,
  GraduationCap,
  Languages,
  MapPin,
  MessageCircle,
  Settings,
  Shield,
  Sparkles,
  Target,
  Users,
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.55, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

function pickText(lang, arText, enText) {
  return lang === 'ar' ? arText : enText;
}

function sanitizeWebsiteCopy(text) {
  if (!text) return '';
  return text
    .replace(/\bBeFluent\b/gi, 'Be Fluent')
    .replace(/\bBefluent\b/gi, 'Be Fluent')
    .replace(/Odoo-managed content/gi, 'published website content')
    .replace(/managed from Odoo/gi, 'managed from the website dashboard')
    .replace(/inside Odoo/gi, 'inside the admissions workflow')
    .replace(/Odoo/gi, 'the website dashboard')
    .replace(/packages from one backend/gi, 'programs from one dashboard');
}

function getIcon(name) {
  return iconMap[name] || Sparkles;
}

function formatPrice(value) {
  return formatNumber(value, { maximumFractionDigits: 2 });
}

function getSectionContent(sections, key) {
  return sections?.[key] || {};
}

function getLocationSlides(location) {
  const slides = Array.isArray(location?.images)
    ? location.images.filter((image) => image?.image_url)
    : [];

  if (slides.length > 0) {
    return slides;
  }

  if (location?.image_url) {
    return [{
      id: `${location.id}-fallback`,
      title: location.name,
      image_url: location.image_url,
    }];
  }

  return [];
}

function stripHtmlTags(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const HeroBackgroundMedia = memo(function HeroBackgroundMedia({
  hero,
  posterUrl,
  sourceCandidates,
  onAllSourcesFailed,
  imageAlt,
}) {
  const videoRef = useRef(null);
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [sourceCandidates]);

  const activeSource = sourceCandidates[sourceIndex] || '';

  const tryPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {});
    }
  };

  const handleError = () => {
    if (sourceIndex < sourceCandidates.length - 1) {
      setSourceIndex((current) => current + 1);
      return;
    }
    onAllSourcesFailed();
  };

  if (hero?.media_type === 'video' && activeSource) {
    return (
      <video
        key={activeSource}
        ref={videoRef}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover [will-change:transform] dark:brightness-[1.08] dark:contrast-[1.04]"
        src={activeSource}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={posterUrl}
        aria-hidden="true"
        onCanPlay={tryPlay}
        onLoadedData={tryPlay}
        onError={handleError}
      />
    );
  }

  return (
    <img
      src={posterUrl}
      alt={imageAlt}
      className="absolute inset-0 h-full w-full object-cover dark:brightness-[1.08] dark:contrast-[1.04]"
    />
  );
});

export default function HomePage() {
  const { lang, t } = useLang();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const [loading, setLoading] = useState(true);
  const [hero, setHero] = useState(null);
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  const [trustBar, setTrustBar] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sections, setSections] = useState({});
  const [campusLocations, setCampusLocations] = useState([]);
  const [campusShowcaseSlides, setCampusShowcaseSlides] = useState([]);
  const [studyBenefits, setStudyBenefits] = useState([]);
  const [keyFacts, setKeyFacts] = useState([]);
  const [whyUs, setWhyUs] = useState([]);
  const [steps, setSteps] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [activeCampusId, setActiveCampusId] = useState(null);
  const [activeShowcaseIndex, setActiveShowcaseIndex] = useState(0);
  const [activeLocationSlides, setActiveLocationSlides] = useState({});
  const [leadForm, setLeadForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    country_code: '+971',
    course_interest: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setHeroVideoFailed(false);

    Promise.all([
      api.get('/content/hero').catch(() => ({ data: null })),
      api.get('/content/trust-bar').catch(() => ({ data: [] })),
      api.get('/courses').catch(() => ({ data: [] })),
      api.get('/content/teachers').catch(() => ({ data: [] })),
      api.get('/content/landing-sections').catch(() => ({ data: {} })),
      api.get('/content/campus-locations').catch(() => ({ data: [] })),
      api.get('/content/campus-showcase').catch(() => ({ data: [] })),
      api.get('/content/study-benefits').catch(() => ({ data: [] })),
      api.get('/content/key-facts').catch(() => ({ data: [] })),
      api.get('/content/why-choose-us').catch(() => ({ data: [] })),
      api.get('/content/steps').catch(() => ({ data: [] })),
      api.get('/content/testimonials').catch(() => ({ data: [] })),
      api.get('/social-posts').catch(() => ({ data: [] })),
    ]).then(([
      heroRes,
      trustRes,
      courseRes,
      teacherRes,
      sectionRes,
      campusRes,
      campusShowcaseRes,
      studyRes,
      factsRes,
      whyRes,
      stepsRes,
      testimonialsRes,
      socialRes,
    ]) => {
      if (!active) return;

      const allCourses = Array.isArray(courseRes.data) ? courseRes.data : [];
      const publishedCourses = allCourses.filter((course) => course?.audience !== 'corporate');
      const locations = Array.isArray(campusRes.data) ? campusRes.data : [];

      setHero(heroRes.data || null);
      setTrustBar(Array.isArray(trustRes.data) ? trustRes.data : []);
      setCourses(publishedCourses);
      setTeachers(Array.isArray(teacherRes.data) ? teacherRes.data : []);
      setSections(sectionRes.data || {});
      setCampusLocations(locations);
      setCampusShowcaseSlides(Array.isArray(campusShowcaseRes.data) ? campusShowcaseRes.data : []);
      setStudyBenefits(Array.isArray(studyRes.data) ? studyRes.data : []);
      setKeyFacts(Array.isArray(factsRes.data) ? factsRes.data : []);
      setWhyUs(Array.isArray(whyRes.data) ? whyRes.data : []);
      setSteps(Array.isArray(stepsRes.data) ? stepsRes.data : []);
      setTestimonials(Array.isArray(testimonialsRes.data) ? testimonialsRes.data : []);
      setSocialPosts(Array.isArray(socialRes.data) ? socialRes.data : []);
      setActiveCampusId(locations[0]?.id || null);
    }).finally(() => {
      if (active) {
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [lang]);

  useEffect(() => {
    setActiveLocationSlides(
      campusLocations.reduce((acc, location) => {
        acc[location.id] = 0;
        return acc;
      }, {})
    );
  }, [campusLocations]);

  useEffect(() => {
    const rotatableLocations = campusLocations.filter((location) => getLocationSlides(location).length > 1);
    if (rotatableLocations.length === 0) return undefined;

    const timer = window.setInterval(() => {
      setActiveLocationSlides((current) => {
        const nextState = { ...current };
        rotatableLocations.forEach((location) => {
          const slides = getLocationSlides(location);
          const currentIndex = current[location.id] || 0;
          nextState[location.id] = (currentIndex + 1) % slides.length;
        });
        return nextState;
      });
    }, 4000);

    return () => window.clearInterval(timer);
  }, [campusLocations]);

  useEffect(() => {
    const slides = campusShowcaseSlides.length > 0
      ? campusShowcaseSlides
      : campusLocations.flatMap((location) => {
          const locationSlides = getLocationSlides(location);
          const description = stripHtmlTags(location.description_html) || location.address;
          if (locationSlides.length > 0) {
            return locationSlides.map((slide, index) => ({
              id: slide.id || `${location.id}-slide-${index}`,
              title: location.name,
              subtitle: location.district,
              description,
              image_url: slide.image_url,
            }));
          }
          return [{
            id: location.id,
            title: location.name,
            subtitle: location.district,
            description,
            image_url: location.image_url,
          }];
        });
    if (slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveShowcaseIndex((current) => (current + 1) % slides.length);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [campusLocations, campusShowcaseSlides]);

  const activeCampus = useMemo(() => {
    return campusLocations.find((item) => item.id === activeCampusId) || campusLocations[0] || null;
  }, [campusLocations, activeCampusId]);

  const locationSection = getSectionContent(sections, 'home_locations');
  const studySection = getSectionContent(sections, 'home_study_city');
  const courseSection = getSectionContent(sections, 'home_courses');
  const teacherSection = getSectionContent(sections, 'home_teachers');
  const stepSection = getSectionContent(sections, 'home_steps');
  const campusSection = getSectionContent(sections, 'home_campus_showcase');
  const keyFactsSection = getSectionContent(sections, 'home_key_facts');
  const whySection = getSectionContent(sections, 'home_why_choose');
  const testimonialsSection = getSectionContent(sections, 'home_testimonials');
  const socialSection = getSectionContent(sections, 'home_social');
  const showcaseSlides = campusShowcaseSlides.length > 0
    ? campusShowcaseSlides
    : campusLocations.flatMap((location) => {
        const locationSlides = getLocationSlides(location);
        const description = stripHtmlTags(location.description_html) || location.address;
        if (locationSlides.length > 0) {
          return locationSlides.map((slide, index) => ({
            id: slide.id || `${location.id}-slide-${index}`,
            title: location.name,
            subtitle: location.district,
            description,
            image_url: slide.image_url,
          }));
        }
        return [{
          id: location.id,
          title: location.name,
          subtitle: location.district,
          description,
          image_url: location.image_url,
        }];
      });
  const activeShowcaseSlide = showcaseSlides[activeShowcaseIndex % Math.max(showcaseSlides.length, 1)] || null;
  const whyChooseCards = [...studyBenefits, ...whyUs];
  const heroShadeStyle = {
    backgroundImage: isDark
      ? 'linear-gradient(180deg, rgba(28,35,51,0.16), rgba(28,35,51,0.48))'
      : 'linear-gradient(180deg, rgba(28,35,51,0.10), rgba(28,35,51,0.50))',
  };
  const heroAtmosphereStyle = {
    backgroundImage: isDark
      ? 'radial-gradient(circle at top left, rgba(108,139,198,0.24), transparent 32%), radial-gradient(circle at 82% 12%, rgba(244,245,247,0.08), transparent 24%), linear-gradient(108deg, rgba(28,35,51,0.54), rgba(28,35,51,0.24) 44%, rgba(28,35,51,0.56))'
      : 'radial-gradient(circle at top left, rgba(108,139,198,0.30), transparent 34%), radial-gradient(circle at 76% 16%, rgba(244,245,247,0.14), transparent 24%), linear-gradient(105deg, rgba(28,35,51,0.56), rgba(28,35,51,0.24) 48%, rgba(28,35,51,0.52))',
  };
  const resolvedHeroTitleLine1 = hero?.title_line1?.toLowerCase().includes('master english')
    ? pickText(lang, 'أتقن اللغات،', 'Master Languages,')
    : (hero?.title_line1 || pickText(lang, 'أتقن اللغات،', 'Master Languages,'));
  const resolvedHeroAccent = hero?.title_accent || pickText(lang, 'افتح', 'Unlock');
  const resolvedHeroTitleLine2 = hero?.title_line2 || pickText(lang, 'مستقبلك', 'Your Future');
  const heroVideoSources = useMemo(() => {
    if (!hero?.video_url) return [];
    const candidates = [buildImageUrl(hero.video_url), hero.video_url].filter(Boolean);
    return [...new Set(candidates)];
  }, [hero?.video_url]);
  const visibleTeachers = useMemo(() => {
    return teachers
      .map((teacher) => ({
        ...teacher,
        image_url: String(teacher?.image_url || '').trim(),
        video_url: String(teacher?.video_url || '').trim(),
      }))
      .filter((teacher) => teacher?.name || teacher?.title || teacher?.short_bio || teacher?.image_url || teacher?.video_url);
  }, [teachers]);

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    if (!leadForm.full_name || !leadForm.email || !leadForm.phone) {
      toast.error(pickText(lang, 'يرجى تعبئة الحقول المطلوبة.', 'Please fill the required fields.'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/leads', {
        ...leadForm,
        source: 'website',
      });
      trackEvent('lead_submit_success', {
        form_id: 'home_lead_form',
        form_type: 'lead_inquiry',
        source_page: '/',
        source_component: 'HomePage',
        route: '/',
        language: lang,
        status: 'success',
      });
      setLeadForm({
        full_name: '',
        email: '',
        phone: '',
        country_code: '+971',
        course_interest: '',
      });
      navigate('/thank-you/inquiry-received', {
        state: {
          form_type: 'lead_inquiry',
          source_page: '/',
          source_component: 'HomePage',
          verified_submission: true,
        },
      });
    } catch {
      toast.error(pickText(lang, 'تعذر إرسال الطلب حالياً.', 'We could not send your inquiry right now.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
          <Skeleton className="h-[620px] rounded-[2rem]" />
          <Skeleton className="h-[620px] rounded-[2rem]" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="bf-page-canvas bf-home-page bg-[#e6e6e6] text-[#231f20] dark:bg-[#231f20] dark:text-[#e6e6e6]">
      {hero && (
        <section className="relative isolate overflow-hidden" data-testid="hero-section">
          {!heroVideoFailed ? (
            <HeroBackgroundMedia
              hero={hero}
              posterUrl={buildImageUrl(hero.image_url)}
              sourceCandidates={heroVideoSources}
              onAllSourcesFailed={() => setHeroVideoFailed(true)}
              imageAlt={t('hero.image_alt')}
            />
          ) : (
            <img
              src={buildImageUrl(hero.image_url)}
              alt={t('hero.image_alt')}
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[1.08] dark:contrast-[1.04]"
            />
          )}
          <div className="pointer-events-none absolute inset-0" style={heroShadeStyle} />
          <div className="pointer-events-none absolute inset-0" style={heroAtmosphereStyle} />

          <div className="relative mx-auto flex min-h-[760px] max-w-7xl items-center px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
            <div className="grid w-full gap-8 lg:grid-cols-[1.05fr_0.55fr] lg:items-center">
              <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0.05} className="max-w-3xl text-white">
                <h1 className="bf-hero-title max-w-4xl">
                  <span>{resolvedHeroTitleLine1}</span>
                  <span className={isDark ? 'text-[#6c8bc6]' : 'text-[#e6e6e6]'}>{resolvedHeroAccent}</span>
                  <span>{resolvedHeroTitleLine2}</span>
                </h1>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.12 }}
                className="lg:justify-self-end"
              >
                <div className="bf-home-floating-card bf-luxury-form-card w-full rounded-[2rem] p-5 text-[#231f20] dark:text-[#e6e6e6] sm:max-w-[430px] sm:p-6">
                  <p className="bf-brand-kicker text-[#6c8bc6]">
                    {hero.form_title || pickText(lang, 'ابدأ رحلتك التعليمية', 'Start your learning journey')}
                  </p>
                  <p className="bf-form-copy mt-3 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
                    {hero.form_subtitle || pickText(lang, 'أرسل طلبك وسيتواصل معك فريقنا بسرعة.', 'Send an inquiry and our advisors will contact you shortly.')}
                  </p>

                  <form onSubmit={handleLeadSubmit} className="mt-5 space-y-3">
                    <Input
                      placeholder={pickText(lang, 'الاسم الكامل', 'Full name')}
                      className="h-12 rounded-2xl border-[#e6e6e6] bg-white dark:border-white/10 dark:bg-white/5 dark:text-[#e6e6e6]"
                      value={leadForm.full_name}
                      onChange={(event) => setLeadForm((current) => ({ ...current, full_name: event.target.value }))}
                    />
                    <div className="grid gap-3 sm:grid-cols-[140px_1fr]">
                      <Select value={leadForm.country_code} onValueChange={(value) => setLeadForm((current) => ({ ...current, country_code: value }))}>
                        <SelectTrigger className="h-12 rounded-2xl border-[#e6e6e6] bg-white dark:border-white/10 dark:bg-white/5 dark:text-[#e6e6e6]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          {countryCodes.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.code} {country.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder={pickText(lang, 'رقم الهاتف', 'Mobile number')}
                        className="h-12 rounded-2xl border-[#e6e6e6] bg-white dark:border-white/10 dark:bg-white/5 dark:text-[#e6e6e6]"
                        value={leadForm.phone}
                        onChange={(event) => setLeadForm((current) => ({ ...current, phone: event.target.value }))}
                      />
                    </div>
                    <Input
                      type="email"
                      placeholder={pickText(lang, 'البريد الإلكتروني', 'Email address')}
                      className="h-12 rounded-2xl border-[#e6e6e6] bg-white dark:border-white/10 dark:bg-white/5 dark:text-[#e6e6e6]"
                      value={leadForm.email}
                      onChange={(event) => setLeadForm((current) => ({ ...current, email: event.target.value }))}
                    />
                    <Select value={leadForm.course_interest} onValueChange={(value) => setLeadForm((current) => ({ ...current, course_interest: value }))}>
                      <SelectTrigger className="h-12 rounded-2xl border-[#e6e6e6] bg-white dark:border-white/10 dark:bg-white/5 dark:text-[#e6e6e6]">
                        <SelectValue placeholder={pickText(lang, 'اختر الدورة', 'Select your course')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.title}>
                            {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="submit" className="bf-premium-cta bf-ui-button h-12 w-full rounded-full bg-[#6c8bc6] text-sm font-semibold text-white hover:bg-[#6c8bc6]" disabled={submitting}>
                      {submitting ? pickText(lang, 'جاري الإرسال...', 'Sending...') : pickText(lang, 'إرسال الطلب', 'Send inquiry')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>

          {trustBar.length > 0 && (
            <div className="bf-hero-trust-orbit relative z-10 mx-auto max-w-7xl px-4 pb-7 sm:px-6 lg:px-8">
              <div className="bf-hero-trust-shell" aria-label={pickText(lang, 'جهات الاعتماد والشركاء', 'Accreditations and partners')}>
                <div className="bf-hero-trust-label">
                  <Shield className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{pickText(lang, 'موثوق ومعتمد', 'Trusted & Accredited')}</span>
                </div>
                <div className="bf-hero-trust-track">
                  <span className="bf-hero-trust-signal" aria-hidden="true" />
                  {trustBar.slice(0, 8).map((item, index) => (
                    <a
                      key={item.id || item.name || index}
                      href={item.link || '#'}
                      className="bf-hero-trust-badge"
                      aria-label={item.name}
                      title={item.name}
                      style={{
                        '--trust-index': index,
                        '--trust-delay': `${index * 85}ms`,
                      }}
                    >
                      <span className="bf-hero-trust-logo-wrap">
                        {item.logo_url ? (
                          <img src={buildImageUrl(item.logo_url)} alt={item.name} className="bf-hero-trust-logo" />
                        ) : (
                          <Shield className="h-4 w-4" aria-hidden="true" />
                        )}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {testimonials.length > 0 && (
        <section className="bf-profile-dark-section px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 max-w-3xl">
              <p className="bf-brand-kicker text-[#8fa9dc]">
                {pickText(lang, 'الشهادات', 'Testimonials')}
              </p>
              <h2 className="bf-brand-title mt-3 text-4xl leading-tight sm:text-5xl lg:text-6xl">
                {sanitizeWebsiteCopy(testimonialsSection.title) || t('testimonials.title')}
              </h2>
              <p className="mt-4 text-base leading-8 text-[#e6e6e6]/78">
                {sanitizeWebsiteCopy(testimonialsSection.subtitle) || pickText(lang, 'قصص وتجارب حقيقية من طلاب Be Fluent.', 'Real student stories from the Be Fluent learning journey.')}
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {testimonials.slice(0, 6).map((item, index) => {
                const imageUrl = buildImageUrl(item.image_url);
                const videoUrl = buildImageUrl(item.video_url);
                const hasVideo = Boolean(videoUrl);

                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: index * 0.08, duration: 0.45 }}
                  >
                    <Card className="bf-testimonial-card h-full overflow-hidden rounded-[1.8rem] border-0 bg-white/8 text-white shadow-none backdrop-blur">
                      <CardContent className="p-6">
                        {hasVideo ? (
                          <div className="bf-testimonial-media is-video relative mb-5 overflow-hidden rounded-[1.2rem] bg-black/40">
                            <video
                              controls
                              preload="metadata"
                              poster={imageUrl || undefined}
                              src={videoUrl}
                              className="h-full w-full object-cover"
                            />
                            <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-2 rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white">
                              <PlayCircle className="h-3.5 w-3.5" />
                              {pickText(lang, 'فيديو', 'Video')}
                            </div>
                          </div>
                        ) : imageUrl ? (
                          <div className="bf-testimonial-media mb-5 overflow-hidden rounded-[1.2rem]">
                            <img src={imageUrl} alt={item.name} className="h-full w-full object-cover" />
                          </div>
                        ) : null}
                        <div className="mb-4 flex gap-1">
                          {Array.from({ length: item.rating || 5 }).map((_, starIndex) => (
                            <Star key={starIndex} className="h-4 w-4 fill-[#e6e6e6] text-[#e6e6e6]" />
                          ))}
                        </div>
                        <div className="mt-6 flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#6c8bc6] font-semibold text-white">
                            {item.name?.split(' ').map((part) => part[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <p className="font-semibold">{item.name}</p>
                            <p className="text-sm text-[#e6e6e6]/72">{item.country}</p>
                            {item.course_label ? (
                              <p className="mt-1 text-xs font-medium tracking-[0.01em] text-[#e6e6e6]/58">
                                {item.course_label}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <TeacherShowcaseSection
        teachers={visibleTeachers}
        lang={lang}
        eyebrow={pickText(lang, 'مدرّسونا', 'Our Instructor')}
        title={sanitizeWebsiteCopy(teacherSection.title) || pickText(lang, 'تعرّف على مدرّسينا', 'Meet Our Teacher')}
        subtitle={sanitizeWebsiteCopy(teacherSection.subtitle) || pickText(
          lang,
          'تعلّم مع مدرّسين ذوي خبرة يقدّمون دعماً واضحاً وتجربة لغوية واثقة وموجّهة نحو التقدّم الحقيقي.',
          'Learn with experienced language instructors who guide each stage with clarity, confidence, and international teaching standards.'
        )}
        badgeLabel={pickText(lang, 'تعليم لغوي متميز', 'Premium Language Teaching')}
      />

      {campusLocations.length > 0 && (
        <section className="bf-location-section px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 max-w-3xl">
              <p className="bf-brand-kicker text-[#6c8bc6]">
                {pickText(lang, 'المواقع', 'Locations')}
              </p>
              <h2 className="bf-section-title mt-3 text-[#231f20] dark:text-[#e6e6e6]">
                {sanitizeWebsiteCopy(locationSection.title) || pickText(lang, 'أين نتواجد', 'Where are we located')}
              </h2>
            </div>
            <div className={`grid gap-6 ${campusLocations.length > 1 ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
              {campusLocations.map((location, index) => {
                const slides = getLocationSlides(location);
                const activeSlideIndex = activeLocationSlides[location.id] || 0;
                const activeSlide = slides[activeSlideIndex % Math.max(slides.length, 1)] || null;
                const locationButtonHref = location.google_maps_url || '';
                const locationButtonLabel = pickText(lang, 'عرض على الخريطة', 'View on Maps');

                return (
                <motion.div
                  key={location.id}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                >
                  <Card className="bf-location-card overflow-hidden rounded-[2rem] border border-[#e6e6e6] bg-white shadow-[0_24px_60px_-38px_rgba(15,23,42,0.4)] dark:border-white/10 dark:bg-[#231f20] dark:shadow-none">
                    <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
                      <div className="bf-location-media relative min-h-[320px] overflow-hidden">
                        {location.media_type === 'video' && location.video_url ? (
                          <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline>
                            <source src={buildImageUrl(location.video_url)} />
                          </video>
                        ) : activeSlide ? (
                          <AnimatePresence mode="wait">
                            <motion.img
                              key={`${location.id}-${activeSlide.id || activeSlideIndex}`}
                              src={buildImageUrl(activeSlide.image_url)}
                              alt={activeSlide.title || location.name}
                              className="absolute inset-0 h-full w-full object-cover"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.8, ease: 'easeInOut' }}
                            />
                          </AnimatePresence>
                        ) : (
                          <div className="absolute inset-0 bg-[#d7dbe2] dark:bg-white/10" />
                        )}
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(108,139,198,0.10),rgba(35,31,32,0.58))]" />
                        <div className="absolute left-5 top-5 rounded-full bg-white/16 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white backdrop-blur">
                          {location.district}
                        </div>
                        {slides.length > 1 ? (
                          <div className="absolute bottom-5 left-5 flex items-center gap-2">
                            {slides.map((slide, slideIndex) => (
                              <button
                                key={slide.id || `${location.id}-dot-${slideIndex}`}
                                type="button"
                                onClick={() => setActiveLocationSlides((current) => ({ ...current, [location.id]: slideIndex }))}
                                className={`h-2.5 rounded-full transition-all ${
                                  slideIndex === activeSlideIndex ? 'w-8 bg-white' : 'w-2.5 bg-white/45'
                                }`}
                                aria-label={`Location image ${slideIndex + 1}`}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <CardContent className="bf-location-content flex flex-col justify-between p-6 sm:p-7">
                        <div>
                          <h3 className="text-2xl font-semibold text-[#231f20] dark:text-[#e6e6e6]">{location.name}</h3>
                          {location.address ? (
                            <p className="mt-4 flex items-start gap-2 text-sm leading-7 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
                              <MapPin className="mt-1 h-4 w-4 shrink-0 text-[#6c8bc6]" />
                              <span className="whitespace-pre-line">{location.address}</span>
                            </p>
                          ) : null}
                          {location.description_html ? (
                            <div
                              className="prose prose-sm mt-5 max-w-none text-[#231f20]/72 dark:text-[#e6e6e6]/72"
                              dangerouslySetInnerHTML={{ __html: location.description_html }}
                            />
                          ) : null}
                        </div>
                        {locationButtonHref ? (
                          <div className="mt-6">
                            <Button asChild className="bf-premium-cta rounded-full px-6">
                              <a href={locationButtonHref} target="_blank" rel="noreferrer">
                                {locationButtonLabel}
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        ) : null}
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {whyChooseCards.length > 0 && (
        <section className="bf-profile-dark-section bf-home-why-section px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 max-w-3xl">
              <p className="bf-brand-kicker text-[#e6e6e6]">
                {pickText(lang, 'لماذا تختارنا', 'Why Choose Us')}
              </p>
              <h2 className="bf-section-title mt-3 text-[#f3f6fb]">
                {sanitizeWebsiteCopy(whySection.title) || pickText(lang, 'لماذا تختار Be Fluent', 'Why choose Be Fluent')}
              </h2>
              <p className="bf-page-copy mt-4 text-[#e6e6e6]/78">
                {whySection.subtitle || pickText(lang, 'منهجية واضحة، بيئة دولية، وتجربة تعليم تدعم التقدم الحقيقي داخل وخارج الصف.', 'A clear learning method, an international environment, and a student experience designed for real progress inside and outside the classroom.')}
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {whyChooseCards.map((item, index) => {
                const Icon = getIcon(item.icon);
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: index * 0.07, duration: 0.45 }}
                  >
                    <Card className="bf-home-value-card h-full rounded-[1.8rem] border border-white/10 bg-white/8 text-white shadow-none backdrop-blur">
                      <CardContent className="p-6">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#6c8bc6]/14 text-[#e6e6e6]">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="bf-card-display mt-5 text-xl">{item.title}</h3>
                        <p className="bf-meta-copy mt-3 text-[#e6e6e6]/74">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}
      {courses.length > 0 && (
        <section className="bf-home-courses-section px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <p className="bf-brand-kicker text-[#6c8bc6]">
                  {pickText(lang, 'الدورات', 'Courses')}
                </p>
                <h2 className="bf-section-title mt-3 text-[#231f20] dark:text-[#e6e6e6]">
                  {courseSection.title || pickText(lang, 'دوراتنا المميزة', 'Our featured courses')}
                </h2>
                <p className="bf-page-copy mt-4 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
                  {courseSection.subtitle || pickText(lang, 'ألقِ نظرة على دوراتنا الشاملة والمنظمة بعناية.', 'Take a look at our comprehensive, well-structured courses.')}
                </p>
              </div>
              <Link to="/courses">
                <Button variant="outline" className="bf-outline-button h-11 rounded-full border-[#231f20]/15 bg-white px-6 text-sm font-semibold text-[#231f20] hover:bg-white dark:border-white/12 dark:bg-white/5 dark:text-[#e6e6e6] dark:hover:bg-white/10">
                  {t('courses.view_all')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {courses.slice(0, 6).map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ delay: index * 0.08, duration: 0.45 }}
                >
                  <Card className="bf-course-card group h-full overflow-hidden rounded-[2rem] border-0 bg-white dark:bg-[#231f20]">
                    <div className="bf-course-card-media relative h-56 overflow-hidden">
                      <img src={buildImageUrl(course.image_url)} alt={course.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(35,31,32,0),rgba(35,31,32,0.52))]" />
                      <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                        <Badge className="rounded-full bg-[#231f20] text-white hover:bg-[#231f20]">{course.modality}</Badge>
                      </div>
                    </div>
                    <CardContent className="bf-course-card-body p-6">
                      <h3 className="bf-course-card-title text-2xl font-semibold text-[#231f20] dark:text-[#e6e6e6]">{course.title}</h3>
                      <p className="mt-3 line-clamp-3 text-sm leading-7 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{course.short_description}</p>
                      <div className="mt-6">
                        <Link to={`/courses/${course.slug}`}>
                          <Button className="bf-premium-cta h-11 rounded-full bg-[#6c8bc6] px-5 text-sm font-semibold text-white hover:bg-[#6c8bc6]">
                            {pickText(lang, 'تفاصيل الدورة', 'Course Details')} <ChevronRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {steps.length > 0 && (
        <section className="bf-steps-section px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 max-w-3xl">
              <p className="bf-brand-kicker text-[#6c8bc6]">
                {pickText(lang, 'الخطوات', 'Steps')}
              </p>
              <h2 className="bf-section-title mt-3 text-[#1c2333] dark:text-[#f4f5f7]">
                {stepSection.title || t('steps.title')}
              </h2>
              <p className="bf-page-copy mt-4 text-[#1c2333]/72 dark:text-[#f4f5f7]/76">
                {stepSection.subtitle || pickText(lang, 'ابدأ رحلتك من اختيار البرنامج حتى تأكيد التسجيل.', 'Start from choosing your program and move through to confirmed enrollment.')}
              </p>
            </div>
            <div className="bf-steps-grid grid gap-5 md:grid-cols-2 xl:grid-cols-5">
              {steps.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ delay: index * 0.08, duration: 0.45 }}
                >
                  <Card className="bf-step-card h-full border-0 shadow-none">
                    <CardContent className="p-6">
                      <div className="bf-step-number">
                        {item.step}
                      </div>
                      <h3 className="bf-card-display mt-5 text-lg text-[#1c2333] dark:text-[#f4f5f7]">{item.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-[#1c2333]/72 dark:text-[#f4f5f7]/72">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {showcaseSlides.length > 0 && activeShowcaseSlide && (
        <section className="bf-learning-space-section px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[0.38fr_0.62fr]">
            <div>
              <p className="bf-brand-kicker text-[#8fa9dc]">
                {pickText(lang, 'المساحة التعليمية', 'Learning Space')}
              </p>
              <h2 className="bf-section-title mt-3 text-[#f4f5f7]">
                {sanitizeWebsiteCopy(campusSection.title) || pickText(lang, 'استكشف مساحتنا التعليمية', 'Check out our learning space')}
              </h2>
              <p className="bf-page-copy mt-4 text-[#f4f5f7]/78">
                {pickText(lang, 'إنه ليس مجرد مكان، بل أكثر من ذلك.', "It's not just a space, it is more")}
              </p>

              <div className="bf-learning-selector mt-8 space-y-3">
                {showcaseSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    onClick={() => setActiveShowcaseIndex(index)}
                    className={`bf-learning-tab w-full px-5 py-4 text-left ${
                      activeShowcaseIndex === index
                        ? 'is-active'
                        : ''
                    }`}
                  >
                    <p className="bf-ui-label text-[#8fa9dc]">{slide.subtitle}</p>
                    <p className="bf-card-display mt-2 text-lg">{slide.title}</p>
                  </button>
                ))}
              </div>
            </div>

            <motion.div
              key={`${activeShowcaseSlide.id || 'slide'}-${activeShowcaseIndex}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="bf-learning-collage"
            >
              {[activeShowcaseSlide, ...showcaseSlides.filter((slide) => slide.id !== activeShowcaseSlide.id)].slice(0, 3).map((slide, index) => (
                <div key={slide.id || `${slide.title}-${index}`} className={`bf-learning-collage-tile tile-${index + 1}`}>
                  <img src={buildImageUrl(slide.image_url)} alt={slide.title} className="h-full w-full object-cover" />
                  <div className="bf-learning-collage-overlay" />
                  <div className="bf-learning-collage-caption">
                    <p className="bf-ui-label text-[#dce6f8]">{slide.subtitle}</p>
                    <h3 className="bf-card-display mt-1 text-xl text-white">{slide.title}</h3>
                  </div>
                </div>
              ))}
              {activeShowcaseSlide.description ? (
                <div className="bf-learning-collage-note">
                  <p className="text-sm leading-7 text-[#f4f5f7]/76">{activeShowcaseSlide.description}</p>
                </div>
              ) : null}
            </motion.div>
          </div>
        </section>
      )}
      {keyFacts.length > 0 && (
        <section className="bf-key-facts-section px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 max-w-3xl">
              <p className="bf-brand-kicker text-[#6c8bc6]">
                {pickText(lang, 'حقائق رئيسية', 'Key Facts')}
              </p>
              <h2 className="bf-section-title mt-3 text-[#1c2333] dark:text-[#f4f5f7]">
                {sanitizeWebsiteCopy(keyFactsSection.title) || pickText(lang, 'حقائق رئيسية عن Be Fluent', 'Key facts of Be Fluent')}
              </h2>
              <p className="bf-page-copy mt-4 text-[#1c2333]/72 dark:text-[#f4f5f7]/72">
                {sanitizeWebsiteCopy(keyFactsSection.subtitle) || pickText(lang, 'قدّم المركز عبر بطاقات واضحة وسريعة القراءة.', 'Present the center through clear, scannable highlight cards.')}
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {keyFacts.map((item, index) => {
                const Icon = getIcon(item.icon);
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: index * 0.07, duration: 0.45 }}
                  >
                    <Card className="bf-fact-card h-full border-0 shadow-none">
                      <CardContent className="p-6">
                        <div className="bf-fact-icon">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="bf-card-display mt-5 text-xl text-[#1c2333] dark:text-[#f4f5f7]">{sanitizeWebsiteCopy(item.title)}</h3>
                        <p className="mt-3 text-sm leading-7 text-[#1c2333]/72 dark:text-[#f4f5f7]/72">{sanitizeWebsiteCopy(item.description)}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {false && whyUs.length > 0 && (
        <section className="bg-white px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
          <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[0.36fr_0.64fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-normal text-[#6c8bc6] sm:text-base">
                {pickText(lang, 'لماذا Be Fluent', 'Why Be Fluent')}
              </p>
              <h2 className="mt-3 text-4xl font-semibold leading-tight tracking-normal text-[#231f20] dark:text-[#e6e6e6] sm:text-5xl lg:text-6xl">
                {sanitizeWebsiteCopy(whySection.title) || pickText(lang, 'لماذا تختار Be Fluent', 'Why choose Be Fluent')}
              </h2>
              <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
                {sanitizeWebsiteCopy(whySection.subtitle) || pickText(lang, 'اجمع بين التميّز الأكاديمي والخدمة الواضحة وتجربة الطالب العملية.', 'Combine academic quality, clear support, and a practical student experience.')}
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              {whyUs.map((item, index) => {
                const Icon = getIcon(item.icon);
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: index * 0.07, duration: 0.45 }}
                  >
                    <Card className="h-full rounded-[1.8rem] border border-[#e6e6e6] bg-[#e6e6e6] shadow-none dark:border-white/10 dark:bg-white/5">
                      <CardContent className="p-6">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="mt-5 text-xl font-semibold text-[#231f20] dark:text-[#e6e6e6]">{item.title}</h3>
                        <p className="mt-3 text-sm leading-7 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {socialPosts.length > 0 && (
        <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-normal text-[#6c8bc6] sm:text-base">
                  {pickText(lang, 'السوشال ميديا', 'Social Media')}
                </p>
                <h2 className="mt-3 text-4xl font-semibold leading-tight tracking-normal text-[#231f20] dark:text-[#e6e6e6] sm:text-5xl lg:text-6xl">
                  {sanitizeWebsiteCopy(socialSection.title) || pickText(lang, 'وسائل التواصل الاجتماعي', 'Our social media')}
                </h2>
                <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
                  {sanitizeWebsiteCopy(socialSection.subtitle) || pickText(lang, 'أحدث المنشورات والأنشطة المرتبطة بالعلامة التجارية.', 'Recent posts and live highlights from the brand.')}
                </p>
              </div>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {socialPosts.slice(0, 6).map((post, index) => (
                <motion.a
                  key={post.id}
                  href={post.post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ delay: index * 0.07, duration: 0.45 }}
                  className="group"
                >
                  <Card className="bf-social-post-card h-full overflow-hidden rounded-[1.8rem] border-0 bg-white dark:bg-[#231f20]">
                    {post.image_url ? (
                      <div className="h-56 overflow-hidden">
                        <img src={buildImageUrl(post.image_url)} alt={post.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    ) : null}
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#231f20]/48 dark:text-[#e6e6e6]/52">
                        <span className="rounded-full bg-[#e6e6e6] px-3 py-1 font-semibold text-[#231f20] dark:bg-white/10 dark:text-[#e6e6e6]">{post.platform}</span>
                        {post.post_date ? <span>{post.post_date}</span> : null}
                      </div>
                      <h3 className="mt-4 text-xl font-semibold text-[#231f20] transition-colors group-hover:text-[#6c8bc6] dark:text-[#e6e6e6]">{post.name}</h3>
                      {post.description ? (
                        <p className="mt-3 text-sm leading-7 text-[#231f20]/72 dark:text-[#e6e6e6]/72">{post.description}</p>
                      ) : null}
                    </CardContent>
                  </Card>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-4 pb-16 pt-14 sm:px-6 sm:pt-16 lg:px-8 lg:pb-24 lg:pt-20">
        <div className="mx-auto max-w-7xl">
          <Card className="bf-luxury-cta overflow-hidden rounded-[2.5rem] border-0 bg-[#6c8bc6] text-white">
            <CardContent className="grid gap-8 p-8 sm:p-10 xl:grid-cols-[1fr_auto] xl:items-center">
              <div>
                <p className="bf-brand-kicker text-white/85">
                  {pickText(lang, 'ابدأ الآن', 'Start Now')}
                </p>
                <h2 className="bf-cta-title mt-3">
                  {pickText(lang, 'أنشئ حسابك، وأكمل اختبار تحديد المستوى، وابدأ الدورة التي تناسب مستواك.', 'Create your account, complete the placement test, and begin the course that matches your level.')}
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-8 text-white/85">
                  {pickText(lang, 'سجّل حسابك، وخض اختبار تحديد المستوى المجاني، ثم تابع إلى البرنامج الذي يناسب مستواك اللغوي وأهدافك التعليمية بأفضل شكل.', 'Register your account, take the free placement test, and continue to the program that best matches your language level and learning goals.')}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row xl:flex-col">
                <Link to="/placement-test">
                  <Button className="bf-premium-cta h-12 rounded-full bg-white px-7 text-sm font-semibold text-[#231f20] hover:bg-white/90">
                    {pickText(lang, 'ابدأ اختبار تحديد المستوى المجاني', 'Start Free Placement Test')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}

